/**
 * What the signed-in panel user may do.
 *
 * The vocabulary itself is generated from `backend/app/core/scopes.py` into
 * `scopes.generated.ts` — there is one list, not two that need reconciling.
 * This file is only the logic on top of it.
 *
 * Everything here decides what the panel *renders*. It decides nothing about
 * what the panel is *allowed* to do: the backend resolves the caller's scopes
 * on every request and refuses regardless of what the UI believed. Getting
 * this wrong shows somebody a button that fails; it never grants access.
 */

import type { UserJwt } from "./types"

export * from "./scopes.generated"

/**
 * The caller's scopes: whatever the backend put on the token, and nothing else.
 *
 * No role fallback, no superuser branch, no `is_staff` shortcut — one rule, so
 * there is nothing here to get out of step with the backend. A token minted
 * before scopes shipped carries none, resolves to nothing, and its holder
 * signs in again; panel sessions are staff sessions and a re-login is cheap.
 *
 * That does mean the backend must deploy before the panel. See the rollout
 * note in brain/guides/admin-panel-permissions.md.
 */
export function grantedScopes(user: UserJwt | null): Set<string> {
  if (!user || user.is_anon || user.scopes === undefined) return new Set()
  return new Set(user.scopes)
}

/** Does this scope set satisfy every one of `required`? */
export function hasScopes(granted: Set<string>, ...required: string[]): boolean {
  return required.every((scope) => granted.has(scope))
}

/** Does this user hold any scope at all, i.e. may they open the panel? */
export function hasPanelAccess(user: UserJwt | null): boolean {
  return grantedScopes(user).size > 0
}
